import React, { type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../components/ui/cn';

/**
 * Telefon ekranlarının ortak parçaları. Renkler tema belirteçlerinden
 * (index.css) geliyor; açık/koyu tema için ayrı sınıf yazmak gerekmiyor.
 */

/** Sekme ekranlarının büyük başlığı; sağda isteğe bağlı düğmeler. */
export const MobileTitle: React.FC<{ title: string; overline?: string; leading?: ReactNode; trailing?: ReactNode }> = ({
  title,
  overline,
  leading,
  trailing,
}) => (
  <div className="flex items-center justify-between gap-3 pt-4">
    <div className="flex items-center gap-2.5 min-w-0">
      {leading}
      <div className="min-w-0">
        {overline && <p className="text-[14px] font-medium text-muted-foreground">{overline}</p>}
        <h1 className="text-[30px] leading-9 font-bold tracking-tight text-foreground truncate">{title}</h1>
      </div>
    </div>
    {trailing && <div className="flex items-center gap-2 shrink-0">{trailing}</div>}
  </div>
);

/**
 * İç ekranların üst çubuğu: solda "‹ Önceki ekran", sağda eylemler.
 * `data-native-back`: telefonun geri tuşu bu düğmeye basar (mobile/src/nativeShell.ts).
 */
export const MobileBackBar: React.FC<{ label: string; onBack: () => void; trailing?: ReactNode }> = ({
  label,
  onBack,
  trailing,
}) => (
  <div className="shrink-0 flex items-center justify-between gap-2 h-[52px] pl-1.5 pr-3 select-none">
    <button
      type="button"
      onClick={onBack}
      data-native-back
      className="h-11 flex items-center gap-0.5 pr-2 text-[16px] font-medium text-brand-text active:opacity-70 min-w-0"
    >
      <ChevronLeft className="w-6 h-6 shrink-0" strokeWidth={2} />
      <span className="truncate">{label}</span>
    </button>
    {trailing && <div className="flex items-center gap-1 shrink-0">{trailing}</div>}
  </div>
);

/** Yuvarlak simge düğmesi (44 px dokunma alanı). */
export const MobileIconButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string; tone?: 'plain' | 'filled' }
> = ({ label, tone = 'filled', className, children, ...rest }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={cn(
      'w-11 h-11 rounded-full flex items-center justify-center text-foreground active:opacity-70 disabled:opacity-40',
      tone === 'filled' && 'bg-secondary',
      className
    )}
    {...rest}
  >
    {children}
  </button>
);

/** Küçük, büyük harfli bölüm başlığı + içerik. */
export const MobileSection: React.FC<{ title?: string; action?: ReactNode; children: ReactNode; className?: string }> = ({
  title,
  action,
  children,
  className,
}) => (
  <section className={cn('space-y-2.5', className)}>
    {(title || action) && (
      <div className="flex items-center justify-between px-1">
        {title && <h2 className="text-[13px] font-semibold tracking-[0.04em] uppercase text-muted-foreground">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

/** Kart zemini: satır listeleri ve kutucuklar için. */
export const MobileCard: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('rounded-[20px] border border-border bg-card text-card-foreground overflow-hidden', className)}>
    {children}
  </div>
);

interface MobileRowProps {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Sağdaki değer ya da rozet. */
  value?: ReactNode;
  onClick?: () => void;
  /** Sağda ok gösterilsin mi (varsayılan: tıklanabiliyorsa). */
  chevron?: boolean;
  tone?: 'default' | 'danger';
  className?: string;
}

/** Kart içindeki satır; tıklanabiliyorsa düğme, değilse düz satır. */
export const MobileRow: React.FC<MobileRowProps> = ({
  icon,
  title,
  subtitle,
  value,
  onClick,
  chevron,
  tone = 'default',
  className,
}) => {
  const body = (
    <>
      {icon && (
        <span className="w-8 h-8 rounded-[9px] bg-secondary flex items-center justify-center shrink-0 text-foreground [&_svg]:w-[18px] [&_svg]:h-[18px]">
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0 flex flex-col gap-0.5 text-left">
        <span className={cn('text-[16px] leading-[21px] truncate', tone === 'danger' ? 'text-record-text font-semibold' : 'text-foreground', subtitle ? 'font-semibold' : '')}>
          {title}
        </span>
        {subtitle && <span className="text-[14px] text-muted-foreground truncate">{subtitle}</span>}
      </span>
      {value && <span className="shrink-0 text-[15px] text-muted-foreground">{value}</span>}
      {(chevron ?? Boolean(onClick)) && <ChevronRight className="w-[18px] h-[18px] shrink-0 text-muted-foreground" />}
    </>
  );
  const base = cn(
    'w-full flex items-center gap-3 px-4 border-b border-border last:border-b-0',
    subtitle ? 'min-h-[68px] py-2.5' : 'min-h-[54px]',
    className
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(base, 'active:bg-secondary/60 transition-colors')}>
      {body}
    </button>
  ) : (
    <div className={base}>{body}</div>
  );
};

/** iOS boyutunda aç/kapa anahtarı (dokunma alanı 44 px). */
export const MobileSwitch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  labelledBy?: string;
  label?: string;
  disabled?: boolean;
}> = ({ checked, onChange, labelledBy, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-labelledby={labelledBy}
    aria-label={labelledBy ? undefined : label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className="h-11 flex items-center shrink-0 disabled:opacity-50"
  >
    <span className={cn('relative w-[51px] h-[31px] rounded-full transition-colors', checked ? 'bg-brand' : 'bg-switch-off')}>
      <span
        className={cn(
          'absolute top-[3px] left-[3px] w-[25px] h-[25px] rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-5'
        )}
      />
    </span>
  </button>
);

/** Üç seçenekli bölümlü düğme (Gün/Hafta/Ay, Özet/Transkript gibi). */
export function MobileSegmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="p-1 rounded-[14px] bg-secondary grid gap-1"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-[38px] rounded-[10px] text-[14.5px] font-semibold transition-colors',
              active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
